import { Tabs } from "@mui/base/Tabs";
import { TabsList } from "@mui/base/TabsList";
import { TabPanel } from "@mui/base/TabPanel";
import { Tab } from "@mui/base/Tab";
import PersonalInfo from "../Components/Kyc/PersonalInfo";
import AdminKyc from "../Components/Kyc/AdminKyc";
import { useUserInfoQuery, useApproveKycMutation, useRejectKycMutation } from "../redux/features/user/userApi";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Security from "../Components/Kyc/Security";
import Navbar from "../Components/Navbar";
import { toast } from "react-toastify";
import { hasPermission } from "../utils/permissionUtils";
import Unauthorized from "./Unauthorized";
import StakeHistory from "../Components/Kyc/StakeHistory";
import CustomModel from "../Components/CustomModel";
import Wallet from "../Components/Kyc/Wallet";
import TranctionH from "../Components/Kyc/TranctionH";
import Nominee from "../Components/Kyc/Nominee";

const Kyc = () => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [updatedStatus, setUpdatedStatus] = useState(null);

  const handleImageViewer = (type) => {
    <CustomModel img={type} setOpen={setOpen} open={open} />
  }

  // Get permissions from localStorage
  const getPermissions = () => {
    const permission = localStorage.getItem('permission');
    if (!permission) return {};
    try {
      return JSON.parse(permission);
    } catch (e) {
      console.error('Error parsing permissions:', e);
      return {};
    }
  };

  const permissions = getPermissions();
  const canReadUserDetails = permissions?.user_details?.read || false;
  const canWriteUserDetails = permissions?.user_details?.write || false;

  const { id } = useParams();
  const [data, setData] = useState([]);

  const { data: userData, isSuccess, refetch } = useUserInfoQuery(id);
  const [approveKyc, { isSuccess: approveSuccess, isError: approveError, data: approveData }] = useApproveKycMutation({});
  const [rejectKyc, { isSuccess: rejectSuccess, isError: rejectError, data: rejectData }] = useRejectKycMutation();

  const handleApprove = async (page) => {
    if (!canWriteUserDetails) {
      toast.error("You don't have permission to approve KYC documents");
      return;
    }
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const result = await approveKyc({
        id,
        page,
        role: localStorage.getItem('role') || 'Unknown'
      }).unwrap();
      if (result.status === true) {
        setUpdatedStatus({
          page: page,
          status: 1,
          verifiedBy: localStorage.getItem('username') || 'Admin',
          role: localStorage.getItem('role') || 'Unknown'
        });
        toast.success(result.message || "KYC document approved successfully");

        try {
          const refetchResult = await refetch();
          if (refetchResult.data) {
            setData(refetchResult.data.data);
          }
        } catch (refetchError) {
          console.error("Error refetching data:", refetchError);
        }
      } else {
        toast.error(result.message || "Failed to approve KYC document");
        setUpdatedStatus(null);
      }
      return result;
    } catch (error) {
      toast.error(error?.data?.message || "Failed to approve KYC document");
      setUpdatedStatus(null);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = (page) => {
    if (!canWriteUserDetails) {
      toast.error("You don't have permission to reject KYC documents");
      return;
    }
    setCurrentPage(page);
    setReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!canWriteUserDetails) {
      toast.error("You don't have permission to reject KYC documents");
      return;
    }
    if (isProcessing) return;

    if (!reason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setIsProcessing(true);
    try {
      const result = await rejectKyc({
        id,
        page: currentPage,
        reason: reason.trim(),
        role: localStorage.getItem('role') || 'Unknown'
      }).unwrap();

      if (result.status === true) {
        setUpdatedStatus({
          page: currentPage,
          status: 0,
          verifiedBy: localStorage.getItem('username') || 'Admin',
          role: localStorage.getItem('role') || 'Unknown'
        });

        toast.success(result.message || "KYC document rejected successfully");
        setRejectDialogOpen(false);
        setReason("");

        try {
          const refetchResult = await refetch();
          if (refetchResult.data) {
            setData(refetchResult.data.data);
          }
        } catch (refetchError) {
          console.error("Error refetching data:", refetchError);
        }
      } else {
        toast.error(result.message || "Failed to reject KYC document");
        setUpdatedStatus(null);
      }
    } catch (error) {
      toast.error(error?.data?.message || "Failed to reject KYC document");
      setUpdatedStatus(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const tab = [
    "Personal Info",
    "Security",
    "Wallet",
    "Stake History",
    "Transaction-history",
    "Referrals",
    "Nominee",
  ];

  useEffect(() => {
    if (isSuccess) {
      setData(userData.data);
    }
  }, [setData, isSuccess]);

  if (!canReadUserDetails) {
    return <Unauthorized />;
  }

  return (
    <>
      <Navbar />
      <Tabs defaultValue={0}>
        <TabsList className="md:w-[80vw] w-full m-auto bg-blue-950 flex font-sans items-center justify-center content-between min-w-tabs-list flex-wrap md:flex-nowrap shadow-lg">
          {tab.map((item, index) => (
            <Tab
              slotProps={{
                root: ({ selected, disabled }) => ({
                  className: `font-sans ${selected
                    ? "text-blue-950 bg-white"
                    : "text-white bg-transparent focus:text-white hover:bg-blue-900"
                    } ${disabled
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                    } text-sm font-bold w-full p-2 m-1.5 border-0 rounded-md flex justify-center focus:outline-0 focus:shadow-outline-blue-light`,
                }),
              }}
              value={index}
              key={index}
            >
              {item}
            </Tab>
          ))}
        </TabsList>

        <TabPanel className="md:w-[80vw] w-full m-auto font-sans text-sm" value={0}>
          <PersonalInfo
            id={id}
            data={data}
            handleApprove={handleApprove}
            handleReject={handleReject}
            isProcessing={isProcessing}
            rejectDialogOpen={rejectDialogOpen}
            setRejectDialogOpen={setRejectDialogOpen}
            reason={reason}
            setReason={setReason}
            handleRejectConfirm={handleRejectConfirm}
            setOpen={setOpen}
            open={open}
            handleImageViewer={handleImageViewer}
            updatedStatus={updatedStatus}
            setUpdatedStatus={setUpdatedStatus}
            canWrite={canWriteUserDetails}
          />

        </TabPanel>
        <TabPanel className="w-[80vw] m-auto font-sans text-sm" value={1}>
          <Security id={id} data={data} canWrite={canWriteUserDetails} />
        </TabPanel>
        <TabPanel className="w-[80vw] m-auto font-sans text-sm" value={2}>
          <Wallet id={id} wallet={userData?.wallet || []} addr={userData?.user_address || []} canWrite={canWriteUserDetails} />
        </TabPanel>
        <TabPanel className="w-[80vw] m-auto font-sans text-sm" value={3}>
          <StakeHistory id={id} canWrite={canWriteUserDetails} />
        </TabPanel>
        <TabPanel className="w-[80vw] m-auto font-sans text-sm" value={4}>
          <TranctionH id={id} canWrite={canWriteUserDetails} />
        </TabPanel>
        <TabPanel className="w-[80vw] m-auto font-sans text-sm" value={5}>
          <div className="p-6 max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="bg-blue-800 text-center px-6 py-4 border-b border-gray-700">
                <h2 className="text-xl font-semibold text-white">Referred By {userData?.referredby?.email || userData?.data?.referraluserid || "NOT Provided"} (From : {userData?.data?.referral_type?.toUpperCase()?.replace("_", " ") || "NOT Provided"})</h2>
                {/* <p className="text-sm text-gray-300">
                  Referred By {userData?.referredby?.email || userData?.data?.referraluserid || "NOT Provided"}
                </p> */}
              </div>
              <div className="p-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  <div className="lg:w-[60%] space-y-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">My Refferals</label>

                    <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
                      {userData?.referred_persons?.map((item, index) => (
                        <div>
                          {index + 1} . {item.email}
                        </div>
                      )) || "No data Found"}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

        </TabPanel>
        <TabPanel className="w-[80vw] m-auto font-sans text-sm" value={6}>
          <Nominee id={id} data={data} canWrite={canWriteUserDetails} />
        </TabPanel>
      </Tabs>

      {/* Reject Dialog */}
      {rejectDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-96">
            <h3 className="text-lg font-bold mb-4">Reject KYC Document</h3>
            <textarea
              className="w-full p-2 border rounded mb-4"
              placeholder="Enter reason for rejection"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows="4"
              disabled={isProcessing}
            />
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                onClick={() => {
                  setRejectDialogOpen(false);
                  setReason("");
                }}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded disabled:opacity-50"
                onClick={handleRejectConfirm}
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Kyc;
